import { attachmentFileHref } from '../../shared/schemas/attachment.ts';
import { photoThumbHref } from '../../shared/schemas/photo.ts';
import {
  type OfflineBundle,
  offlineBundleSchema,
  syncManifestSchema,
} from '../../shared/schemas/sync.ts';
import { bundleReads } from './bundle.ts';
import { OFFLINE_BLOB_MAX_BYTES, OFFLINE_PROPERTY_BLOB_MAX_BYTES } from './constants.ts';
import { setOfflineState } from './state.ts';
import {
  dropBlobsOf,
  dropEntitiesUnder,
  forgetKept,
  putBlob,
  putEntity,
  readMeta,
  rememberKept,
  writeMeta,
} from './store.ts';
import type { KeptProperty } from './db.ts';

// Keeping a property offline, refreshing what is kept, and forgetting it.
//
// "Keep offline" reads the whole property in one request
// (`/api/properties/:id/offline`), writes every page's read into the copy,
// and fetches each photo's thumbnail and each file's bytes up to the
// ceilings in constants.ts — a 400 MB video on a phone with 2 GB free is not
// a kindness, so anything over the per-file ceiling, and everything past the
// per-property total, is named as skipped rather than silently missing.
//
// Refreshing asks the manifest what has moved since the last one and
// re-reads any kept property that is named in it. The manifest reports a
// deleted node by its own id and not its subtree, and reports a note, photo,
// file, link, or power feed as having touched the node it hangs off, so
// re-reading the whole property is the honest response to either.

/**
 * The path segments a kept read can begin with. A node id is unique across
 * the map, so trying each segment against it costs nothing and needs no
 * record of which kind of node an id belonged to.
 */
const KEPT_SEGMENTS = [
  'properties',
  'rooms',
  'racks',
  'items',
  'drops',
  'checklists',
  'records',
] as const;

/** Fetch a bundle and answer it parsed; throws like any other read. */
async function fetchBundle(propertyId: string): Promise<OfflineBundle> {
  const response = await fetch(`/api/properties/${propertyId}/offline`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`The property could not be read for keeping (${String(response.status)})`);
  }
  return offlineBundleSchema.parse(await response.json());
}

interface Media {
  readonly key: string;
  readonly href: string;
  readonly name: string;
  readonly declaredBytes: number;
}

/** Everything with bytes worth keeping: a thumbnail per photo, a file per attachment. */
function mediaOf(bundle: OfflineBundle): Media[] {
  return [
    ...bundle.photos.map((photo) => ({
      key: `photos/${photo.id}/thumb`,
      href: photoThumbHref(photo.id),
      name: photo.caption === '' ? 'a photo' : photo.caption,
      declaredBytes: 0,
    })),
    ...bundle.attachments.map((file) => ({
      key: `attachments/${file.id}/file`,
      href: attachmentFileHref(file.id),
      name: file.title === '' ? file.filename : file.title,
      declaredBytes: file.sizeBytes,
    })),
  ];
}

/** Fetch and keep what fits; answers the bytes kept and what was left behind. */
async function keepMedia(
  propertyId: string,
  media: readonly Media[],
): Promise<{ bytes: number; skipped: string[] }> {
  let bytes = 0;
  const skipped: string[] = [];
  for (const entry of media) {
    if (entry.declaredBytes > OFFLINE_BLOB_MAX_BYTES) {
      skipped.push(entry.name);
      continue;
    }
    if (bytes >= OFFLINE_PROPERTY_BLOB_MAX_BYTES) {
      skipped.push(entry.name);
      continue;
    }
    const response = await fetch(entry.href, { credentials: 'same-origin' });
    if (!response.ok) {
      skipped.push(entry.name);
      continue;
    }
    const blob = await response.blob();
    if (blob.size > OFFLINE_BLOB_MAX_BYTES || bytes + blob.size > OFFLINE_PROPERTY_BLOB_MAX_BYTES) {
      skipped.push(entry.name);
      continue;
    }
    await putBlob({ key: entry.key, blob, propertyId, sizeBytes: blob.size });
    bytes += blob.size;
  }
  return { bytes, skipped };
}

/**
 * Keep one property on this device: every read its pages make, and the
 * pictures and files that fit. Answers what was kept, so the page can say
 * what it skipped.
 */
export async function keepProperty(propertyId: string): Promise<KeptProperty> {
  const bundle = await fetchBundle(propertyId);
  const keptAt = new Date().toISOString();
  const reads = bundleReads(bundle, keptAt);
  for (const read of reads) {
    await putEntity(read);
  }
  await dropBlobsOf(propertyId);
  const { bytes, skipped } = await keepMedia(propertyId, mediaOf(bundle));
  const kept: KeptProperty = {
    propertyId,
    name: bundle.property.name,
    keptAt,
    bytes,
    skipped,
    nodeIds: nodeIdsOf(reads.map((read) => read.key)),
  };
  await rememberKept(kept);
  const meta = await readMeta();
  await writeMeta({ ...meta, syncedAt: bundle.at });
  setOfflineState({ syncedAt: bundle.at });
  return kept;
}

/**
 * Stop keeping a property: every read of it, every read of everything under
 * it, its pictures, and its place in the list. The rooms, racks, devices and
 * runs are kept under their own keys rather than under the property's, so
 * dropping the property's own key alone would leave the site behind; the
 * record of what was kept names every node id, which is what this walks.
 */
export async function forgetProperty(propertyId: string): Promise<void> {
  const meta = await readMeta();
  const kept = meta.kept.find((entry) => entry.propertyId === propertyId);
  for (const id of kept?.nodeIds ?? [propertyId]) {
    for (const segment of KEPT_SEGMENTS) {
      await dropEntitiesUnder(`${segment}/${id}`);
    }
  }
  await dropBlobsOf(propertyId);
  await forgetKept(propertyId);
}

/**
 * Bring every kept property up to date. The manifest says what has moved
 * since the last refresh; any kept property named in it — by one of its own
 * rows changing, by something under it being deleted, or by a note or photo
 * touching it — is read again in full. With nothing kept, or nothing moved,
 * this costs one request.
 */
export async function refreshKept(): Promise<number> {
  const meta = await readMeta();
  if (meta.kept.length === 0) {
    return 0;
  }
  if (meta.syncedAt === null) {
    return await refreshAllKept(meta.kept);
  }
  const response = await fetch(`/api/sync/manifest?since=${encodeURIComponent(meta.syncedAt)}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`The list of changes could not be read (${String(response.status)})`);
  }
  const manifest = syncManifestSchema.parse(await response.json());
  // A truncated manifest has not said everything that moved, so every kept
  // property is read again rather than guessing at the rest.
  const moved = new Set([
    ...manifest.changed.map((entry) => entry.id),
    ...manifest.deleted.map((entry) => entry.id),
    ...manifest.touched,
  ]);
  const stale = manifest.truncated ? meta.kept : meta.kept.filter((kept) => touches(kept, moved));
  const refreshed = await refreshAllKept(stale);
  const after = await readMeta();
  await writeMeta({ ...after, syncedAt: manifest.at });
  setOfflineState({ syncedAt: manifest.at });
  return refreshed;
}

/**
 * The node ids a set of read keys names: the id of every key that is a node
 * of its own (`rooms/<id>`), and not of the lists that hang off one
 * (`rooms/<id>/notes`).
 */
function nodeIdsOf(keys: readonly string[]): string[] {
  const ids = new Set<string>();
  for (const key of keys) {
    const parts = key.split('/');
    const id = parts[1];
    if (parts.length === 2 && id !== undefined) {
      ids.add(id);
    }
  }
  return [...ids];
}

/** Whether anything the manifest reported as moved is a node this property keeps. */
function touches(kept: KeptProperty, moved: ReadonlySet<string>): boolean {
  return moved.has(kept.propertyId) || kept.nodeIds.some((id) => moved.has(id));
}

async function refreshAllKept(kept: readonly KeptProperty[]): Promise<number> {
  let refreshed = 0;
  for (const entry of kept) {
    await keepProperty(entry.propertyId);
    refreshed += 1;
  }
  return refreshed;
}
